import {NavLink} from '@wandb/weave/common/util/links';
import {Icon} from '@wandb/weave/components/Icon';
import React from 'react';

import {useViewer} from '../../../state/viewer/hooks';
import {Viewer} from '../../../state/viewer/types';
import {WORKSPACE_PLACEHOLDER_NAME} from '../../../state/workspaces/namedWorkspaceActionsInternal';
import {NamedWorkspaceId} from '../../../state/workspaces/types';
import {getNewNwUrlSearch, ParamNames} from '../../../state/workspaces/url';
import {DateFormat, format, getLocalYear} from '../../../util/date';
import {NamedWorkspaceMenuItem} from '../types';
import {isUnnamedWorkspace} from '../utils';
import {UserProfileImage} from './UserProfileImage';

type NamedWorkspaceMenuItemProps = {
  option: NamedWorkspaceMenuItem;
};

export const NamedWorkspaceMenuItemComp = (
  props: NamedWorkspaceMenuItemProps
) => {
  const {option} = props;

  return (
    <NavLink
      className="flex grow text-moon-800 dark:text-moon-300"
      to={getMenuItemLinkTo(option.namedWorkspaceId)}>
      {option.optionType === 'personal' ? (
        <PersonalItem option={option} />
      ) : (
        <SharedItem option={option} />
      )}
    </NavLink>
  );
};

const PersonalItem = ({option}: NamedWorkspaceMenuItemProps) => {
  return (
    <>
      <UserProfileImage src={option.photoUrl} alt={option.displayName} />
      <div className="mx-8 flex flex-col p-0 text-base leading-6">
        <p className="max-w-[13rem] grow overflow-hidden text-ellipsis whitespace-nowrap">
          {option.displayName}
        </p>
        {option.updatedAt != null && (
          <p className="shrink-0 text-sm text-moon-500 ">
            Auto-saved on {formatDate(option.updatedAt)}
          </p>
        )}
      </div>
    </>
  );
};

const SharedItem = ({option}: NamedWorkspaceMenuItemProps) => {
  const viewer = useViewer();
  const lastSavedUserDisplayName = getUserDisplayName(
    viewer,
    option.updatedBy,
    option.createdBy
  );

  return (
    <>
      <div className="h-24 w-24">
        <Icon name="view-glasses" width={18} height={18} />
      </div>
      <div
        className="mx-8 flex flex-col p-0 text-base leading-6"
        data-test="named-workspace-menu-item">
        <p className="max-w-[13rem] grow overflow-hidden text-ellipsis whitespace-nowrap text-moon-800 dark:text-moon-300">
          {isUnnamedWorkspace(option.displayName)
            ? WORKSPACE_PLACEHOLDER_NAME
            : option.displayName}
        </p>
        {option.updatedAt != null && (
          <p className="shrink-0 text-sm text-moon-500">
            Saved on {formatDate(option.updatedAt)}{' '}
            {lastSavedUserDisplayName !== '' &&
              `by ${lastSavedUserDisplayName}`}
          </p>
        )}
      </div>
    </>
  );
};

function getMenuItemLinkTo(namedWorkspaceId: NamedWorkspaceId) {
  return getNewNwUrlSearch({
    [ParamNames.nwId]: namedWorkspaceId.id,
  });
}

function getUserDisplayName(
  viewer: Viewer | undefined,
  updatedBy: NamedWorkspaceMenuItem['updatedBy'],
  createdBy: NamedWorkspaceMenuItem['createdBy']
) {
  const updatedByDisplayName = getDisplayName(viewer, updatedBy?.username);
  if (updatedByDisplayName == null || updatedByDisplayName === '') {
    // fall back to createdBy username because someone might not have
    // clickked on "Save view" yet
    return getDisplayName(viewer, createdBy?.username);
  }
  return updatedByDisplayName;
}
function getDisplayName(
  viewer: Viewer | undefined,
  username: string | undefined
) {
  if (username == null) {
    return '';
  }
  if (username === viewer?.username) {
    return 'you';
  }
  return username;
}

/**
 * Example of formatted date:
 * Nov 03 at 5:09pm OR Nov 03, 23 at 5:09pm
 */
export function formatDate(d: string | Date) {
  const date = new Date(d);
  const time = ' at ' + format(date, DateFormat.TIME);

  if (getLocalYear(date) !== getLocalYear(new Date())) {
    return format(date, DateFormat.MONTH_DAY_SHORT_YEAR) + time;
  }

  return format(date, DateFormat.MONTH_DAY) + time;
}
